import React, {
  useState, useEffect, useCallback, RefObject,
} from 'react';
import { IconButton } from '@material-ui/core';
import videojs from 'video.js';
import clsx from 'clsx';

import { isMobileOnly } from 'react-device-detect';
import { PlayIcon, PauseIcon } from '../../icon/solid';
import useStyles from './styles';

type Props = {
  instancePlayer: videojs.Player;
  refPlayer: RefObject<HTMLVideoElement>;
};

const MainPlayActionVideoJS = ({ refPlayer, instancePlayer }: Props) => {
  const classes = useStyles();
  const [isError, setIsError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(!instancePlayer.paused());
  const player = refPlayer.current;

  const playHandler = useCallback(() => {
    instancePlayer.playsinline(true);
    instancePlayer.play.call(instancePlayer);
    setIsPlaying(true);
  }, [instancePlayer, setIsPlaying]);

  const pauseHandler = useCallback(() => {
    instancePlayer.pause.call(instancePlayer);
    setIsPlaying(false);
  }, [instancePlayer, setIsPlaying]);

  const mouseOverPause = useCallback((e: React.MouseEvent) => {
    if (isPlaying) {
      const target = e.target as HTMLButtonElement;
      target.style.opacity = '1';
    }
  }, [isPlaying]);

  const mouseLeavePause = useCallback((e: React.MouseEvent) => {
    if (isPlaying) {
      const target = e.target as HTMLButtonElement;
      target.style.opacity = '0';
    }
  }, [isPlaying]);

  const toggleVideo = useCallback(() => {
    if (isPlaying) {
      pauseHandler();
    } else {
      playHandler();
    }
  }, [isPlaying, pauseHandler, playHandler]);

  useEffect(
    () => {
      const handleError = () => setIsError(true);
      if (player) {
        player.addEventListener('error', handleError);
      }

      return () => {
        if (player) {
          player.removeEventListener('error', handleError);
        }
      };
    },
    [setIsError, player],
  );

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        if (isPlaying) {
          pauseHandler();
        } else {
          playHandler();
        }
      }
    };
    window.addEventListener('keydown', keyDownHandler);
    return () => window.removeEventListener('keydown', keyDownHandler);
  }, [isPlaying, playHandler, pauseHandler]);

  useEffect(() => {
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    instancePlayer.on('play', onPlay);
    instancePlayer.on('pause', onPause);
    return () => {
      instancePlayer.off('play', onPlay);
      instancePlayer.off('pause', onPause);
    };
  }, [instancePlayer, setIsPlaying]);

  if (isError) {
    return null;
  }

  return (
    <div
      onClick={() => toggleVideo()}
      className={clsx(classes.buttonWrapper, {
        [classes.mobileButtonWrapper]: isMobileOnly,
      })}
    >
      {
        isPlaying
          ? (
            <IconButton
              onClick={pauseHandler}
              onMouseOver={mouseOverPause}
              onMouseLeave={mouseLeavePause}
              className={clsx(classes.mainPlayButton, classes.mainPauseButton, {
                [classes.mobileMainPlayButton]: isMobileOnly,
              })}
              key="player-js-pause"
            >
              <PauseIcon className={classes.mainPlayIcon} />
            </IconButton>
          )
          : (
            <IconButton
              onClick={playHandler}
              className={clsx(classes.mainPlayButton, {
                [classes.mobileMainPlayButton]: isMobileOnly,
              })}
              key="player-js-play"
            >
              <PlayIcon className={classes.mainPlayIcon} />
            </IconButton>
          )
      }
    </div>
  );
};

export default MainPlayActionVideoJS;
